import React, { useMemo } from 'react'
import styles from './PrivateQuotationCardUnified.module.scss'
import { CalendarDays, MapPin, Cuboid, DollarSign, Building2, User, ExternalLink, MessageCircle } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { connect } from 'react-redux'
import { useActionGuard } from '../../hooks/useActionGuard.ts'
import { openSendContactNotificationModal } from '../../store/actions'

type Jurisdiction = {
  name?: string | null
} | null

type Organism = {
  name?: string | null
} | null

type MaterialCategory = {
  name?: string | null
} | null

type Material = {
  category?: MaterialCategory
} | null

type SubQuotationMaterial = {
  material?: Material
} | null

type SubQuotation = {
  subQuotation_materials?: SubQuotationMaterial[] | null
} | null

type QuotationUser = {
  id?: number | string | null
  type?: string | null
  razon_social?: string | null
  name?: string | null
  last_name?: string | null
} | null

type PrivateQuotation = {
  id: number | string
  name?: string | null
  zone?: string | null
  startDate?: string | null
  endDate?: string | null
  currency?: string | null
  organismo?: Organism
  jurisdiccion?: Jurisdiction
  user?: QuotationUser
  description?: string | null
  subquotation?: SubQuotation[] | null
}

interface PrivateQuotationCardUnifiedProps {
  item: PrivateQuotation
  userData?: { id?: number | string | null } | null
  openSendContactNotificationModal: (
    user: unknown,
    extra?: { relatedMaterialsQuotationName?: string | null }
  ) => void
}

const formatDate = (value?: string | null) => {
  if (!value) {
    return ''
  }
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) {
    return ''
  }
  const day = date.getUTCDate().toString().padStart(2, '0')
  const month = (date.getUTCMonth() + 1).toString().padStart(2, '0')
  const year = date.getUTCFullYear()
  return `${day}/${month}/${year}`
}

const PrivateQuotationCardUnified: React.FC<PrivateQuotationCardUnifiedProps> = ({
  item,
  userData,
  openSendContactNotificationModal: openContactModal,
}) => {
  const navigate = useNavigate()
  const { guard } = useActionGuard()

  const requesterName = useMemo(() => {
    if (!item?.user) {
      return 'Sin especificar'
    }
    if (item.user.type === 'COMPANY') {
      return (item.user.razon_social || '').trim() || 'Sin especificar'
    }
    const firstName = item.user.name || ''
    const lastName = item.user.last_name || ''
    const full = `${firstName} ${lastName}`.trim()
    return full || 'Sin especificar'
  }, [item?.user])

  const categoryNames = useMemo(() => {
    if (!Array.isArray(item?.subquotation)) {
      return [] as string[]
    }
    const names = item.subquotation.flatMap(subquotation => {
      if (!Array.isArray(subquotation?.subQuotation_materials)) {
        return [] as string[]
      }
      return subquotation.subQuotation_materials
        .map(material => material?.material?.category?.name)
        .filter((name): name is string => Boolean(name))
    })
    return Array.from(new Set(names))
  }, [item?.subquotation])

  const publisherId = item?.user?.id
  const hasPublisherProfile =
    publisherId !== undefined && publisherId !== null && String(publisherId).length > 0

  const isMyQuotation = useMemo(() => {
    const uid = userData?.id
    if (uid === undefined || uid === null) {
      return false
    }
    if (publisherId === undefined || publisherId === null) {
      return false
    }
    return String(uid) === String(publisherId)
  }, [userData?.id, publisherId])

  const startDate = formatDate(item?.startDate)
  const endDate = formatDate(item?.endDate)
  const periodLabel =
    startDate && endDate ? `${startDate} - ${endDate}` : startDate || endDate || 'Sin fecha definida'

  const locationLabel = item?.jurisdiccion?.name
    ? `${item.jurisdiccion.name}${item?.zone ? ` - ${item.zone}` : ''}`
    : item?.zone || 'Sin especificar'

  const primaryCtaLabel = isMyQuotation ? 'Ir a mi cotización' : 'Quiero cotizar'
  const primaryCtaAriaLabel = isMyQuotation
    ? `Abrir tu cotización ${item?.name || 'de materiales'}`
    : `Cotizar en ${item?.name || 'cotización de materiales'}`

  const handleNavigate = () => {
    if (isMyQuotation) {
      navigate(`/cotizaciondemateriales/${item?.id}`)
    } else {
      navigate(`/cotizaciondemateriales/postulante/${item?.id}`)
    }
  }

  const handleViewPublisherProfile = () => {
    guard('view_profile', () => {
      navigate(`/perfil/${String(publisherId)}`)
    })()
  }

  const handleContactPublisher = () => {
    if (!item.user) {
      return
    }
    guard('send_message', () => {
      openContactModal(item.user, { relatedMaterialsQuotationName: item.name ?? '' })
    })()
  }

  return (
    <article className={styles.card}>
      <div className={styles.lead}>
        <h3 className={styles.title}>{item?.name}</h3>
        {item?.description ? <p className={styles.description}>{item.description}</p> : null}
      </div>
      <div className={styles.body}>
        <div className={styles.infoSection}>
          <div className={styles.infoGrid}>
            <div className={styles.infoItem}>
              <div className={styles.infoIcon}>
                <User size={16} />
              </div>
              <div className={styles.infoContent}>
                <span className={styles.infoLabel}>Solicitante</span>
                <span className={styles.infoValue}>{requesterName}</span>
              </div>
            </div>
            <div className={styles.infoItem}>
              <div className={styles.infoIcon}>
                <MapPin size={16} />
              </div>
              <div className={styles.infoContent}>
                <span className={styles.infoLabel}>Ubicación</span>
                <span className={styles.infoValue}>{locationLabel}</span>
              </div>
            </div>
            {item?.organismo?.name ? (
              <div className={styles.infoItem}>
                <div className={styles.infoIcon}>
                  <Building2 size={16} />
                </div>
                <div className={styles.infoContent}>
                  <span className={styles.infoLabel}>Organismo</span>
                  <span className={styles.infoValue}>{item.organismo.name}</span>
                </div>
              </div>
            ) : null}
            <div className={styles.infoItem}>
              <div className={styles.infoIcon}>
                <CalendarDays size={16} />
              </div>
              <div className={styles.infoContent}>
                <span className={styles.infoLabel}>Período de cotización</span>
                <span className={styles.infoValue}>{periodLabel}</span>
              </div>
            </div>
            <div className={styles.infoItem}>
              <div className={styles.infoIcon}>
                <DollarSign size={16} />
              </div>
              <div className={styles.infoContent}>
                <span className={styles.infoLabel}>Moneda</span>
                <span className={styles.infoValue}>{item?.currency || 'Sin especificar'}</span>
              </div>
            </div>
          </div>
          <div className={styles.materialsBlock}>
            <div className={styles.materialsHeader}>
              <div className={styles.infoIcon}>
                <Cuboid size={16} />
              </div>
              <span className={styles.materialsTitle}>Materiales a cotizar</span>
            </div>
            {categoryNames.length > 0 ? (
              <div className={styles.tags}>
                {categoryNames.map(category => (
                  <span key={category} className={styles.tag}>
                    {category}
                  </span>
                ))}
              </div>
            ) : (
              <p className={styles.materialsEmpty}>Sin categorías asociadas</p>
            )}
          </div>
        </div>
      </div>
      <div className={styles.footer}>
        {hasPublisherProfile && !isMyQuotation ? (
          <>
            <button
              type="button"
              className={styles.ctaSecondary}
              onClick={handleViewPublisherProfile}
              aria-label={`Ver perfil del solicitante de ${item?.name || 'cotización'}`}
            >
              <User size={16} aria-hidden />
              <span>Ver perfil</span>
            </button>
            <button
              type="button"
              className={styles.ctaSecondary}
              onClick={handleContactPublisher}
              aria-label={`Contactar al solicitante de ${item?.name || 'cotización'}`}
            >
              <MessageCircle size={16} aria-hidden />
              <span>Contactar</span>
            </button>
          </>
        ) : null}
        <button
          type="button"
          className={styles.cta}
          onClick={guard('send_quotation', handleNavigate)}
          aria-label={primaryCtaAriaLabel}
        >
          <span>{primaryCtaLabel}</span>
          <ExternalLink size={16} />
        </button>
      </div>
    </article>
  )
}

export default connect(null, { openSendContactNotificationModal })(PrivateQuotationCardUnified)
